/**
 * Storage Yard Service
 *
 * Handles CRUD operations for storage yard management.
 * Follows the same patterns as resource-container.service.ts
 */

import { apiClient } from './api';
import type { CreateStorageYardDTO, UpdateStorageYardDTO } from '../schemas/storageYardSchema';
import type { Container } from './resource-container.service';

/**
 * Storage yard entity
 */
export interface StorageYard {
  id: string;
  providerId: string;
  name: string;
  address: string;
  region?: string;
  isDefault: boolean;
  isActive: boolean;
  capacity?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Storage yard list response
 */
export interface StorageYardListResponse {
  yards: StorageYard[];
  total: number;
  summary: {
    totalYards: number;
    activeYards: number;
    totalContainers: number;
    defaultYard?: StorageYard;
  };
}

/**
 * Storage yard detail response
 */
export interface StorageYardDetailResponse {
  yard: StorageYard;
}

/**
 * Yard containers response
 */
export interface YardContainersResponse {
  containers: Container[];
  total: number;
  yardId: string;
}

/**
 * Storage Yard Service
 */
class StorageYardService {
  private baseUrl = '/provider/resources/storage-yards';

  /**
   * List all storage yards
   */
  async list(): Promise<StorageYardListResponse> {
    return apiClient.get<StorageYardListResponse>(this.baseUrl);
  }

  /**
   * Get storage yard by ID
   */
  async getById(id: string): Promise<StorageYard> {
    const response = await apiClient.get<StorageYardDetailResponse>(
      `${this.baseUrl}/${id}`
    );
    return response.yard;
  }

  /**
   * Get containers in a specific yard
   */
  async getYardContainers(yardId: string): Promise<Container[]> {
    const response = await apiClient.get<YardContainersResponse>(
      `${this.baseUrl}/${yardId}/containers`
    );
    return response.containers;
  }

  /**
   * Create a new storage yard
   */
  async create(data: CreateStorageYardDTO): Promise<StorageYard> {
    const response = await apiClient.post<StorageYardDetailResponse>(
      this.baseUrl,
      data
    );
    return response.yard;
  }

  /**
   * Update storage yard details
   */
  async update(id: string, data: UpdateStorageYardDTO): Promise<StorageYard> {
    const response = await apiClient.put<StorageYardDetailResponse>(
      `${this.baseUrl}/${id}`,
      data
    );
    return response.yard;
  }

  /**
   * Delete storage yard (soft delete)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }
}

// Export singleton instance
export const storageYardService = new StorageYardService();
